import React, { FC } from 'react';
import { ComponentConsumerProps, isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import { TProductModelDetailsSpecificationTableProps } from './types/index.types';
import { TProductModelDetailsSpecificationTableModel } from './models/index.model';
import Heading from '../@shared/heading';
import Accordion from '../@shared/accordion/accordionMap';
import AccordionItem from '../@shared/accordion/accordionItem';
import AccordionColumns from '../@shared/accordion/accordionColumns';
import Picture from '../@shared/picture';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { eyebrowKeyModelSpecifications, titleKeyModelSpecifications } from '../../constants/dictionary.constants';
import './styles.scss';

const ProductModelDetailsSpecificationTable: FC<ComponentConsumerProps & TProductModelDetailsSpecificationTableProps> = (
  props: TProductModelDetailsSpecificationTableProps
) => {
  const model = new TProductModelDetailsSpecificationTableModel(props);
  const apiResponse = isEmptyResponse(props);
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const imageURL = model.desktopBgImage?.url || model.mobileBgImage?.url;

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <div className="model-details-specification-table-wrapper product-white-spacer">
      {model?.groups?.map((card, index) => (
        <section key={index} className="model-details-specification-table-wrapper__section">
          <div className="model-details-specification-table-wrapper__bgWrapper">
            {imageURL && (
              <Picture
                productImageProps={{
                  desktopRendition: model.desktopBgImage,
                  mobileRendition: model.mobileBgImage,
                  pictureClassName: 'model-details-specification-table-wrapper__bgImage',
                }}
              />
            )}
            <Heading
              title={card.title}
              richTextTitle={true}
              eyebrow={card.eyebrow}
              useDictionaryFallback
              eyebrowDictionaryKey={eyebrowKeyModelSpecifications}
              titleDictionaryKey={titleKeyModelSpecifications}
            />
          </div>
          <Accordion defaultIndex={index === 0 ? '0' : ''} istoggleIndependent="true">
            {card.items?.map((item) => (
              <AccordionItem index={`${item?.id}`} header={item.title}>
                <AccordionColumns columnData={item} />
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ))}
    </div>
  );
};

export default withSitecoreContext()(ProductModelDetailsSpecificationTable);
